import { useState } from "react";
import {
  ghostBtnClass,
  inputClass,
  mfgSecondaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import { useManufacturingDictateSession } from "./useManufacturingDictateSession";
import { extractSeriesSizeLadderFromTranscript } from "@/lib/manufacturing/seriesSizeLadderClient";

/** Sizes field + dictate → AI size-ladder parse for the series wizard. */
export function ManufacturingModelsSeriesWizardSizesField({
  sizesRaw,
  onSizesRawChange,
}: {
  sizesRaw: string;
  onSizesRawChange: (next: string) => void;
}) {
  const dictate = useManufacturingDictateSession();
  const [brief, setBrief] = useState("");
  const [parseBusy, setParseBusy] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [parseMeta, setParseMeta] = useState<string | null>(null);

  const transcript = (dictate.transcript.trim() || brief.trim()).trim();

  async function parseSizes() {
    if (!transcript) {
      setParseError("Dictate or paste a size offering description first.");
      return;
    }
    setParseBusy(true);
    setParseError(null);
    setParseMeta(null);
    if (dictate.state === "recording") dictate.stop();
    const res = await extractSeriesSizeLadderFromTranscript(transcript);
    setParseBusy(false);
    if (!res.ok) {
      setParseError(res.error);
      return;
    }
    onSizesRawChange(res.data.sizes_raw);
    const n = res.data.sizes.length;
    const src = res.data.source === "llm" ? "AI" : "heuristic";
    setParseMeta(`Parsed ${n} size${n === 1 ? "" : "s"} (${src}). Review before Create series.`);
    if (!brief.trim() && dictate.transcript.trim()) setBrief(dictate.transcript.trim());
  }

  return (
    <div className="space-y-2">
      <label className="block text-sm">
        <span className="text-slate-600 dark:text-slate-300">Sizes</span>
        <input
          className={`${inputClass} mt-1`}
          value={sizesRaw}
          onChange={(e) => onSizesRawChange(e.target.value)}
          placeholder="comma-separated sizes (W×L) — or dictate below"
        />
      </label>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/60">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300">
            Dictate size ladder
          </span>
          {dictate.isSupported ? (
            <>
              <button
                type="button"
                className={ghostBtnClass}
                disabled={parseBusy || dictate.state === "recording"}
                onClick={() => {
                  setParseError(null);
                  dictate.start();
                }}
              >
                {dictate.state === "recording" ? "Listening…" : "Mic"}
              </button>
              <button
                type="button"
                className={ghostBtnClass}
                disabled={parseBusy || dictate.state !== "recording"}
                onClick={() => dictate.stop()}
              >
                Stop
              </button>
            </>
          ) : (
            <span className="text-xs text-slate-500">Mic not supported — paste text below.</span>
          )}
          <button
            type="button"
            className={mfgSecondaryBtnClass}
            disabled={parseBusy || !transcript}
            onClick={() => void parseSizes()}
          >
            {parseBusy ? "Parsing…" : "Parse sizes"}
          </button>
        </div>
        <textarea
          className={`${inputClass} mt-2 min-h-[88px] w-full`}
          value={dictate.state === "recording" || dictate.transcript.trim() ? dictate.transcript : brief}
          onChange={(e) => {
            setBrief(e.target.value);
            if (dictate.transcript) dictate.reset();
          }}
          placeholder='e.g. “We offer six by eight… ten by twelve moving up in four foot increments to ten by twenty-four…”'
          disabled={dictate.state === "recording"}
        />
        {dictate.error ? (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">Dictation: {dictate.error}</p>
        ) : null}
        {parseError ? (
          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{parseError}</p>
        ) : null}
        {parseMeta ? (
          <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">{parseMeta}</p>
        ) : null}
      </div>
    </div>
  );
}
